import React, { useState, useEffect } from 'react';
import { isValidArweave, isValidChia, isValidFilecoin } from '../utils/validators';
import { Profile } from '../types';

interface WalletSetupModalProps {
  show: boolean;
  profile: Profile;
  jwt: string | null;
  onClose: () => void;
  onSave: (xch: string, fil: string, ar: string) => Promise<void>;
}

const WalletSetupModal: React.FC<WalletSetupModalProps> = ({
  show,
  profile,
  jwt,
  onClose,
  onSave
}) => {
  const [xchInput, setXchInput] = useState("");
  const [filInput, setFilInput] = useState("");
  const [arInput, setArInput] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Animation control
  useEffect(() => {
    if (show) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [show]);

  if (!show) return null;

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      setError("");
      setXchInput("");
      setFilInput("");
      setArInput("");
    }, 300);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError("");

      if (!jwt) {
        setError("Authentication required. Please log in again.");
        return;
      }

      const needsXch = !profile.xch_wallet;
      const needsFil = !profile.fil_wallet;
      const needsAr = !profile.ar_wallet;

      if (needsXch && !isValidChia(xchInput)) {
        setError("Please provide a valid Chia (XCH) wallet address.");
        return;
      }

      if (needsFil && !isValidFilecoin(filInput)) {
        setError("Please provide a valid Filecoin (FIL) wallet address.");
        return;
      }

      if (needsAr && !isValidArweave(arInput)) {
        setError("Please provide a valid Arweave (AR) wallet address.");
        return;
      }

      await onSave(xchInput.trim(), filInput.trim(), arInput.trim());
      handleClose();
    } catch (e: any) {
      setError(e?.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const needsXch = !profile.xch_wallet;
  const needsFil = !profile.fil_wallet;
  const needsAr = !profile.ar_wallet;
  const isXchValid = !needsXch || isValidChia(xchInput);
  const isFilValid = !needsFil || isValidFilecoin(filInput);
  const isArValid = !needsAr || isValidArweave(arInput);
  const missingWalletCount = [needsXch, needsFil, needsAr].filter(Boolean).length;
  const canSave = isXchValid && isFilValid && isArValid && Boolean(xchInput.trim() || filInput.trim() || arInput.trim());

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-md transition-opacity duration-300"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-2xl transform transition-all duration-300 ${
        isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-8'
      }`}>
        
        {/* Glow Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 
                        rounded-2xl blur-xl opacity-60" />
        
        {/* Main Modal Content */}
        <div className="relative bg-gradient-to-br from-gray-900/95 to-gray-800/95 
                        backdrop-blur-xl border border-gray-700/50 rounded-2xl 
                        shadow-2xl overflow-hidden">
          
          {/* Header */}
          <div className="relative p-6 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 
                          border-b border-gray-700/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 
                                rounded-xl border border-cyan-500/30">
                  <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 
                                 bg-clip-text text-transparent">
                    Wallet Configuration Required
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Complete setup to activate staking features
                  </p>
                </div>
              </div>
              
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 
                           rounded-lg transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            
            {/* Progress Indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                needsXch ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'
              }`} />
              <div className="w-12 h-0.5 bg-gray-600" />
              <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                needsFil ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'
              }`} />
              <div className="w-12 h-0.5 bg-gray-600" />
              <div className={`w-3 h-3 rounded-full transition-all duration-300 ${
                needsAr ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'
              }`} />
              <div className="ml-4 text-sm text-gray-300">
                {missingWalletCount > 0
                  ? `${missingWalletCount} wallet${missingWalletCount === 1 ? '' : 's'} required`
                  : 'Setup complete'}
              </div>
            </div>

            {/* XCH Wallet Input */}
            {needsXch && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 
                                  rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">XCH</span>
                  </div>
                  <div>
                    <label className="text-lg font-semibold text-white">
                      Chia Wallet Address
                    </label>
                    <p className="text-sm text-gray-400">
                      Your Chia (XCH) wallet address for reward distribution
                    </p>
                  </div>
                </div>
                
                <div className="relative group">
                  <div className={`absolute inset-0 rounded-xl blur-md transition-all duration-300 ${
                    xchInput && isValidChia(xchInput) 
                      ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 opacity-100' 
                      : xchInput && !isValidChia(xchInput)
                      ? 'bg-gradient-to-r from-red-500/20 to-rose-500/20 opacity-100'
                      : 'opacity-0'
                  }`} />
                  
                  <input
                    type="text"
                    placeholder="xch1abcdef... (enter your Chia wallet address)"
                    value={xchInput}
                    onChange={(e) => setXchInput(e.target.value)}
                    className={`relative w-full bg-gray-800/50 backdrop-blur-sm rounded-xl px-4 py-4
                               font-mono text-sm transition-all duration-200
                               focus:outline-none focus:ring-2 placeholder-gray-500
                               ${xchInput && isValidChia(xchInput)
                                 ? 'border border-green-500/50 text-green-300 focus:ring-green-500/30' 
                                 : xchInput && !isValidChia(xchInput)
                                 ? 'border border-red-500/50 text-red-300 focus:ring-red-500/30'
                                 : 'border border-gray-600/50 text-white hover:border-gray-500/50 focus:ring-cyan-500/30'
                               }`}
                  />
                  
                  {/* Validation Icon */}
                  {xchInput && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      {isValidChia(xchInput) ? (
                        <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
                
                {xchInput && !isValidChia(xchInput) && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 
                                  rounded-lg">
                    <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-300">
                      This doesn't appear to be a valid Chia wallet address. Please check and try again.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* FIL Wallet Input */}
            {needsFil && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-600 
                                  rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">FIL</span>
                  </div>
                  <div>
                    <label className="text-lg font-semibold text-white">
                      Filecoin Wallet Address
                    </label>
                    <p className="text-sm text-gray-400">
                      Your Filecoin (FIL) wallet address for reward distribution
                    </p>
                  </div>
                </div>
                
                <div className="relative group">
                  <div className={`absolute inset-0 rounded-xl blur-md transition-all duration-300 ${
                    filInput && isValidFilecoin(filInput) 
                      ? 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 opacity-100' 
                      : filInput && !isValidFilecoin(filInput)
                      ? 'bg-gradient-to-r from-red-500/20 to-rose-500/20 opacity-100'
                      : 'opacity-0'
                  }`} />
                  
                  <input
                    type="text"
                    placeholder="f1... / f3... / f4... (enter your Filecoin wallet address)"
                    value={filInput}
                    onChange={(e) => setFilInput(e.target.value)}
                    className={`relative w-full bg-gray-800/50 backdrop-blur-sm rounded-xl px-4 py-4
                               font-mono text-sm transition-all duration-200
                               focus:outline-none focus:ring-2 placeholder-gray-500
                               ${filInput && isValidFilecoin(filInput)
                                 ? 'border border-purple-500/50 text-purple-300 focus:ring-purple-500/30' 
                                 : filInput && !isValidFilecoin(filInput)
                                 ? 'border border-red-500/50 text-red-300 focus:ring-red-500/30'
                                 : 'border border-gray-600/50 text-white hover:border-gray-500/50 focus:ring-cyan-500/30'
                               }`}
                  />
                  
                  {/* Validation Icon */}
                  {filInput && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      {isValidFilecoin(filInput) ? (
                        <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
                
                {filInput && !isValidFilecoin(filInput) && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 
                                  rounded-lg">
                    <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-300">
                      This doesn't appear to be a valid Filecoin wallet address. Please check and try again.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* AR Wallet Input */}
            {needsAr && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-sky-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">AR</span>
                  </div>
                  <div>
                    <label className="text-lg font-semibold text-white">
                      Arweave Wallet Address
                    </label>
                    <p className="text-sm text-gray-400">
                      Your Arweave (AR) wallet address for reward distribution
                    </p>
                  </div>
                </div>

                <div className="relative group">
                  <div className={`absolute inset-0 rounded-xl blur-md transition-all duration-300 ${
                    arInput && isValidArweave(arInput)
                      ? 'bg-gradient-to-r from-sky-500/20 to-blue-500/20 opacity-100'
                      : arInput && !isValidArweave(arInput)
                      ? 'bg-gradient-to-r from-red-500/20 to-rose-500/20 opacity-100'
                      : 'opacity-0'
                  }`} />

                  <input
                    type="text"
                    placeholder="43-character Arweave wallet address"
                    value={arInput}
                    onChange={(e) => setArInput(e.target.value)}
                    className={`relative w-full bg-gray-800/50 backdrop-blur-sm rounded-xl px-4 py-4
                               font-mono text-sm transition-all duration-200
                               focus:outline-none focus:ring-2 placeholder-gray-500
                               ${arInput && isValidArweave(arInput)
                                 ? 'border border-sky-500/50 text-sky-300 focus:ring-sky-500/30'
                                 : arInput && !isValidArweave(arInput)
                                 ? 'border border-red-500/50 text-red-300 focus:ring-red-500/30'
                                 : 'border border-gray-600/50 text-white hover:border-gray-500/50 focus:ring-cyan-500/30'
                               }`}
                  />

                  {arInput && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      {isValidArweave(arInput) ? (
                        <svg className="w-5 h-5 text-sky-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>

                {arInput && !isValidArweave(arInput) && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-300">
                      Arweave addresses contain exactly 43 letters, digits, dashes or underscores and are case-sensitive.
                    </p>
                  </div>
                )}
                {arInput && isValidArweave(arInput) && (
                  <p className="text-xs text-sky-300/70">
                    Address structure accepted. New wallets with zero balance and no transactions are supported.
                  </p>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 
                              rounded-xl">
                <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-red-300 font-medium">Setup Error</p>
                  <p className="text-red-200 text-sm mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Security Notice */}
            <div className="p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div>
                  <p className="text-cyan-300 font-medium text-sm">Security Notice</p>
                  <p className="text-cyan-200 text-sm mt-1">
                    Your wallet addresses are encrypted and stored securely. They are only used for reward distribution and never for any other purpose.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 bg-gray-900/50 border-t border-gray-700/50">
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white 
                           rounded-xl font-semibold transition-all duration-200 
                           border border-gray-600 hover:border-gray-500"
              >
                Cancel Setup
              </button>
              
              <button
                onClick={handleSave}
                disabled={isSaving || !canSave}
                className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 
                           hover:from-cyan-600 hover:to-blue-700 text-white font-semibold 
                           rounded-xl transition-all duration-200 transform hover:scale-105 
                           hover:shadow-lg hover:shadow-cyan-500/25 
                           disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                           flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Saving Configuration...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Save & Activate Staking</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletSetupModal;
